if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.verticalThumbnailMql = window.matchMedia('(min-width: 990px)');
        this.sliderButtons = this.elements.viewer?.querySelector('.product__media-slider-buttons');
        this.setupSliderArrows();
        this.setupVerticalThumbnailSlider();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupVerticalThumbnailSlider() {
        if (!this.elements.thumbnails || !this.classList.contains('gallery_layout_vertical')) return;

        this.thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        this.thumbnailPrev = this.elements.thumbnails.querySelector('button[name="previous"]');
        this.thumbnailNext = this.elements.thumbnails.querySelector('button[name="next"]');

        if (!this.thumbnailList || !this.thumbnailPrev || !this.thumbnailNext) return;

        this.onVerticalThumbnailNav = this.onVerticalThumbnailNav.bind(this);
        this.updateVerticalThumbnailButtons = this.updateVerticalThumbnailButtons.bind(this);

        this.thumbnailPrev.addEventListener('click', this.onVerticalThumbnailNav, true);
        this.thumbnailNext.addEventListener('click', this.onVerticalThumbnailNav, true);
        this.thumbnailList.addEventListener(
          'scroll',
          debounce(this.updateVerticalThumbnailButtons, 50),
          { passive: true }
        );
        this.verticalThumbnailMql.addEventListener('change', this.updateVerticalThumbnailButtons);

        this.thumbnailResizeObserver = new ResizeObserver(this.updateVerticalThumbnailButtons);
        this.thumbnailResizeObserver.observe(this.thumbnailList);

        this.updateVerticalThumbnailButtons();
      }

      getThumbnailScrollStep() {
        const item = this.thumbnailList?.querySelector('.thumbnail-list__item');
        if (!item) return 0;

        const listStyles = window.getComputedStyle(this.thumbnailList);
        const gap = parseFloat(listStyles.rowGap || listStyles.gap) || 12;
        return item.offsetHeight + gap;
      }

      onVerticalThumbnailNav(event) {
        if (!this.verticalThumbnailMql.matches || !this.classList.contains('gallery_layout_vertical')) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const step = parseInt(event.currentTarget.dataset.step, 10) || 1;
        const scrollAmount = this.getThumbnailScrollStep() * step;

        this.thumbnailList.scrollBy({
          top: event.currentTarget.name === 'next' ? scrollAmount : -scrollAmount,
          behavior: 'smooth',
        });
      }

      updateVerticalThumbnailButtons() {
        if (!this.thumbnailList || !this.thumbnailPrev || !this.thumbnailNext) return;

        if (!this.verticalThumbnailMql.matches || !this.classList.contains('gallery_layout_vertical')) {
          this.thumbnailPrev.removeAttribute('disabled');
          this.thumbnailNext.removeAttribute('disabled');
          return;
        }

        const maxScroll = this.thumbnailList.scrollHeight - this.thumbnailList.clientHeight;

        if (maxScroll <= 1) {
          this.thumbnailPrev.setAttribute('disabled', 'disabled');
          this.thumbnailNext.setAttribute('disabled', 'disabled');
          return;
        }

        if (this.thumbnailList.scrollTop <= 1) {
          this.thumbnailPrev.setAttribute('disabled', 'disabled');
        } else {
          this.thumbnailPrev.removeAttribute('disabled');
        }

        if (this.thumbnailList.scrollTop >= maxScroll - 1) {
          this.thumbnailNext.setAttribute('disabled', 'disabled');
        } else {
          this.thumbnailNext.removeAttribute('disabled');
        }
      }

      setupSliderArrows() {
        if (!this.sliderButtons || !this.elements.viewer) return;

        this.positionSliderArrows = this.positionSliderArrows.bind(this);
        this.mql.addEventListener('change', this.positionSliderArrows);
        this.elements.viewer.addEventListener('slideChanged', this.onViewerSlideChanged.bind(this));

        this.gallerySlider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (this.gallerySlider) {
          this.gallerySlider.addEventListener(
            'scroll',
            debounce(() => {
              this.syncActiveSlide(this.getActiveSlide());
              if (this.mql.matches) this.positionSliderArrows();
              this.updateGalleryNavButtons();
            }, 50),
            { passive: true }
          );
        }

        this.prevNavButton = this.sliderButtons.querySelector('[name="previous"]');
        this.nextNavButton = this.sliderButtons.querySelector('[name="next"]');

        [this.prevNavButton, this.nextNavButton].forEach((button) => {
          if (!button) return;
          button.addEventListener('click', this.onGalleryNavClick.bind(this), true);
        });

        this.sliderArrowsResizeObserver = new ResizeObserver(() => {
          this.positionSliderArrows();
          this.updateGalleryNavButtons();
        });
        this.sliderArrowsResizeObserver.observe(this.elements.viewer);

        this.positionSliderArrows();
        this.updateGalleryNavButtons();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.positionSliderArrows();
            this.updateGalleryNavButtons();
          });
        });
      }

      getGallerySlides() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item')).filter((slide) => {
          return slide.clientWidth > 0 && window.getComputedStyle(slide).display !== 'none';
        });
      }

      onGalleryNavClick(event) {
        if (!this.mql.matches) {
          window.setTimeout(() => {
            this.syncActiveSlide(this.getActiveSlide());
            this.positionSliderArrows();
            this.updateGalleryNavButtons();
          }, 100);
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const slides = this.getGallerySlides();
        const currentIndex = slides.indexOf(this.getActiveSlide());
        if (currentIndex === -1) return;

        if (event.currentTarget.name === 'next' && currentIndex < slides.length - 1) {
          this.setActiveMedia(slides[currentIndex + 1].dataset.mediaId, false);
        } else if (event.currentTarget.name === 'previous' && currentIndex > 0) {
          this.setActiveMedia(slides[currentIndex - 1].dataset.mediaId, false);
        }
      }

      updateGalleryNavButtons() {
        if (!this.prevNavButton || !this.nextNavButton) return;

        const slides = this.getGallerySlides();
        if (slides.length < 2) {
          this.prevNavButton.setAttribute('disabled', 'disabled');
          this.nextNavButton.setAttribute('disabled', 'disabled');
          return;
        }

        const activeSlide = this.getActiveSlide();
        const activeIndex = slides.indexOf(activeSlide);

        if (activeIndex <= 0) {
          this.prevNavButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevNavButton.removeAttribute('disabled');
        }

        if (activeIndex >= slides.length - 1) {
          this.nextNavButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextNavButton.removeAttribute('disabled');
        }
      }

      onViewerSlideChanged(event) {
        this.syncActiveSlide(event.detail?.currentElement);
        this.positionSliderArrows();
        this.updateGalleryNavButtons();
      }

      syncActiveSlide(slide) {
        if (!slide || !this.elements.viewer) return;

        this.elements.viewer.querySelectorAll('.product__media-item').forEach((element) => {
          element.classList.remove('is-active');
        });
        slide.classList.add('is-active');

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
          if (thumbnail) this.setActiveThumbnail(thumbnail);
        }

        this.updateGalleryNavButtons();
      }

      getActiveSlide() {
        const slides = this.getGallerySlides();
        const activeSlide = this.elements.viewer.querySelector('.product__media-item.is-active');

        if (activeSlide && slides.includes(activeSlide)) {
          return activeSlide;
        }

        const slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');

        if (!slider || slides.length < 2) {
          return slides[0] || activeSlide;
        }

        const sliderRect = slider.getBoundingClientRect();
        const sliderCenter = sliderRect.left + sliderRect.width / 2;
        let closestSlide = slides[0];
        let closestDistance = Infinity;

        slides.forEach((slide) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - sliderCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSlide = slide;
          }
        });

        return closestSlide;
      }

      positionSliderArrows() {
        if (!this.sliderButtons || !this.elements.viewer) return;

        if (!this.mql.matches) {
          this.sliderButtons.style.top = '';
          this.sliderButtons.style.height = '';
          return;
        }

        const activeSlide = this.getActiveSlide();
        const mediaContainer = activeSlide?.querySelector('.product-media-container');

        if (!mediaContainer) return;

        const viewerRect = this.elements.viewer.getBoundingClientRect();
        const mediaRect = mediaContainer.getBoundingClientRect();
        const top = Math.max(0, mediaRect.top - viewerRect.top);
        const height = mediaRect.height;

        this.sliderButtons.style.top = `${top}px`;
        this.sliderButtons.style.height = `${height}px`;

        const activeImage = mediaContainer.querySelector('img');
        if (activeImage && !activeImage.complete) {
          activeImage.addEventListener('load', this.positionSliderArrows, { once: true });
        }
      }

      onSlideChanged(event) {
        this.syncActiveSlide(event.detail?.currentElement);
        this.positionSliderArrows();
        this.updateGalleryNavButtons();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) {
            this.positionSliderArrows();
            this.updateGalleryNavButtons();
            return;
          }
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
          this.positionSliderArrows();
          this.updateGalleryNavButtons();
        });
        this.playActiveMedia(activeMedia);
        this.positionSliderArrows();
        this.updateGalleryNavButtons();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
          this.positionSliderArrows();
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      disconnectedCallback() {
        if (this.sliderArrowsResizeObserver) this.sliderArrowsResizeObserver.disconnect();
        if (this.thumbnailResizeObserver) this.thumbnailResizeObserver.disconnect();
      }
    }
  );
}
